import type { ReactNode } from 'react';
import { paraMetni } from '../rapor-ekrani/bicim';
import type { HucreDegeri } from '../rapor-ekrani/kolon';
import { CINS_ADI, payMetni, sureMetni, type Cins } from './tipler';

/**
 * S340 Hamam Performans: ekranın küçük görsel parçaları (onaylı demo `tasks/hamam-performans/sablon.html`).
 * Renkler demonun açık temasından; taraf rengi ekranın her yerinde aynıdır (rozet, çubuk, lejant).
 */
export const RENK = {
  K: { yazi: '#b0477e', zemin: '#f8e9f1' },
  E: { yazi: '#2f62a8', zemin: '#e6eef9' },
  '-': { yazi: '#7b8591', zemin: '#eceff2' },
  nakit: '#3f8f5b',
  kart: '#6a5fb0',
} as const;

export function Rozet({ cins }: { cins: Cins }) {
  return (
    <span
      className="inline-block rounded-full px-2 py-px text-[12px] font-semibold leading-5"
      style={{ background: RENK[cins].zemin, color: RENK[cins].yazi }}
    >
      {CINS_ADI[cins]}
    </span>
  );
}

/**
 * Tablonun süre hücresi ("1 sa 57 dk"; ölçü yoksa "-"). Sayı hücreleri eş aralıklı yazıyla basılır; süre
 * metni o yazıyla 96 px'e sığmıyordu (ölçüldü: 10 harf x 7,9 px), panelin düz yazısıyla basılır.
 */
export const sureHucresi = (v: HucreDegeri): ReactNode => (
  <span className="font-sans">{sureMetni(typeof v === 'number' ? v : null)}</span>
);

/** Özet kutusu: küçük etiket, büyük değer, altında tek satır açıklama. */
export function Kutu({ etiket, alt, kucuk = false, children }: { etiket: string; alt?: string; kucuk?: boolean; children: ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-lg px-3 py-2 min-w-0">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-ink-500">{etiket}</span>
      <b className={`block mono font-semibold text-ink-900 leading-snug break-words ${kucuk ? 'text-[15px]' : 'text-[18px]'}`}>{children}</b>
      {alt && <small className="block text-[12px] text-ink-500">{alt}</small>}
    </div>
  );
}

/** Bölüm kartı: başlık solda, araçlar (seçici, lejant) sağda; gövde altında. */
export function Kart({ baslik, alt, araclar, testid, children }: {
  baslik: ReactNode;
  alt?: string;
  araclar?: ReactNode;
  testid?: string;
  children: ReactNode;
}) {
  return (
    <section data-testid={testid} className="bg-surface border border-border rounded-lg min-w-0">
      <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 px-3.5 pt-3">
        <div className="min-w-0">
          {typeof baslik === 'string' ? <h2 className="m-0 text-[15px] font-semibold text-ink-900">{baslik}</h2> : baslik}
          {alt && <p className="m-0 mt-0.5 text-[13px] text-ink-500">{alt}</p>}
        </div>
        {araclar}
      </header>
      <div className="px-3.5 pt-2.5 pb-3.5">{children}</div>
    </section>
  );
}

const Nokta = ({ renk }: { renk: string }) => (
  <i aria-hidden="true" className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: renk }} />
);

/** Lejant satırı: çubuk grafiğin üstünde taraf renkleri. */
export function Lejant({ ogeler }: { ogeler: ReadonlyArray<{ ad: string; renk: string }> }) {
  return (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[12px] text-ink-500">
      {ogeler.map((o) => (
        <span key={o.ad}><Nokta renk={o.renk} />{o.ad}</span>
      ))}
    </div>
  );
}

/** Nakit / kart şeridi ve altında tutarlarıyla payları. Pay, nakit + kart toplamına göredir. */
export function OdemeSeridi({ nakit, kart }: { nakit: number; kart: number }) {
  const toplam = nakit + kart;
  const en = (v: number) => `${toplam > 0 ? (v / toplam) * 100 : 0}%`;
  return (
    <>
      <div
        role="img"
        aria-label={`Nakit ${payMetni(nakit, toplam)}, kart ${payMetni(kart, toplam)}`}
        className="flex h-2.5 rounded-full overflow-hidden bg-ink-100 mt-2.5 mb-1.5"
      >
        <i className="block h-full" style={{ width: en(nakit), background: RENK.nakit }} />
        <i className="block h-full" style={{ width: en(kart), background: RENK.kart }} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink-500" data-testid="odeme-yazi">
        <span><Nokta renk={RENK.nakit} />Nakit <b className="mono font-semibold text-ink-900">{paraMetni(nakit)}</b> {payMetni(nakit, toplam)}</span>
        <span><Nokta renk={RENK.kart} />Kart <b className="mono font-semibold text-ink-900">{paraMetni(kart)}</b> {payMetni(kart, toplam)}</span>
      </div>
    </>
  );
}
